import * as yup from 'yup';

import { requiredString } from 'utils/constants';
import { formatToBrowserDate } from 'utils/dates';

export const FormFields = {
  siteCode: 'siteCode',
  mrn: 'mrn',
  sendingFacility: 'sendingFacility',
  alternateMrn: 'alternateMrn',
  familyName: 'familyName',
  givenName: 'givenName',
  middleName: 'middleName',
  prefix: 'prefix',
  suffix: 'suffix',
  dob: 'dob',
  gender: 'gender',
  raceId: 'raceId',
  raceText: 'raceText',
  street: 'street',
  city: 'city',
  province: 'province',
  zip: 'zip',
  countryCode: 'countryCode',
  phoneNumberHome: 'phoneNumberHome',
  emailAddressHome: 'emailAddressHome',
  phoneNumberBusiness: 'phoneNumberBusiness',
  emailAddressBusiness: 'emailAddressBusiness',
  primaryLanguageId: 'primaryLanguageId',
  primaryLanguageText: 'primaryLanguageText',
  maritalStatus: 'maritalStatus',
  religion: 'religion',
  ssn: 'ssn',
  dln: 'dln',
  ethnicGroupId: 'ethnicGroupId',
  ethnicGroupText: 'ethnicGroupText',
  publicityIndicator: 'publicityIndicator',
};

export const genderOptions = [
  { value: 'M', text: 'Male (M)' },
  { value: 'F', text: 'Female (F)' },
  { value: 'U', text: 'Unknown (U)' },
];

const genderValues = genderOptions.map(({ value }) => value);

export const schema = yup.object().shape({
  // Required fields
  [FormFields.siteCode]: yup
    .string()
    .trim()
    .required(requiredString)
    .max(100, ({ max }) => `Site Code should not exceed ${max} characters`)
    .matches(
      /^[^A-Za-z]/,
      'Site Code must start with a number or special character',
    ),
  [FormFields.mrn]: yup
    .string()
    .trim()
    .required(requiredString)
    .max(100, ({ max }) => `MRN should not exceed ${max} characters`),
  [FormFields.sendingFacility]: yup
    .string()
    .trim()
    .required(requiredString)
    .max(50, ({ max }) => `Sending Facility should not exceed ${max} characters`),
  [FormFields.familyName]: yup
    .string()
    .trim()
    .required(requiredString)
    .max(60, ({ max }) => `Family Name should not exceed ${max} characters`),
  [FormFields.givenName]: yup
    .string()
    .trim()
    .required(requiredString)
    .max(60, ({ max }) => `Given Name should not exceed ${max} characters`),
  [FormFields.dob]: yup
    .date()
    .nullable()
    .typeError('Invalid date format')
    .required(requiredString),
  // Optional fields
  [FormFields.alternateMrn]: yup
    .string()
    .trim()
    .max(100, ({ max }) => `Alternate MRN should not exceed ${max} characters`),
  [FormFields.middleName]: yup
    .string()
    .trim()
    .max(60, ({ max }) => `Middle Name should not exceed ${max} characters`),
  [FormFields.prefix]: yup
    .string()
    .trim()
    .max(10, ({ max }) => `Prefix should not exceed ${max} characters`),
  [FormFields.suffix]: yup
    .string()
    .trim()
    .max(10, ({ max }) => `Suffix should not exceed ${max} characters`),
  [FormFields.gender]: yup
    .string()
    .trim()
    .oneOf(['', ...genderValues], 'Gender must be M, F or U'),
  [FormFields.raceId]: yup
    .string()
    .trim()
    .max(20, ({ max }) => `Race ID should not exceed ${max} characters`),
  [FormFields.raceText]: yup
    .string()
    .trim()
    .max(200, ({ max }) => `Race Text should not exceed ${max} characters`),
  [FormFields.street]: yup
    .string()
    .trim()
    .max(80, ({ max }) => `Street should not exceed ${max} characters`),
  [FormFields.city]: yup
    .string()
    .trim()
    .max(60, ({ max }) => `City should not exceed ${max} characters`),
  [FormFields.province]: yup
    .string()
    .trim()
    .max(60, ({ max }) => `Province should not exceed ${max} characters`),
  [FormFields.zip]: yup
    .string()
    .trim()
    .max(20, ({ max }) => `ZIP should not exceed ${max} characters`),
  [FormFields.countryCode]: yup
    .string()
    .trim()
    .max(4, ({ max }) => `Country Code should not exceed ${max} characters`),
  [FormFields.phoneNumberHome]: yup
    .string()
    .trim()
    .max(40, ({ max }) => `Home Phone should not exceed ${max} characters`),
  [FormFields.emailAddressHome]: yup
    .string()
    .trim()
    .email('Invalid email format')
    .max(90, ({ max }) => `Home Email should not exceed ${max} characters`),
  [FormFields.phoneNumberBusiness]: yup
    .string()
    .trim()
    .max(40, ({ max }) => `Business Phone should not exceed ${max} characters`),
  [FormFields.emailAddressBusiness]: yup
    .string()
    .trim()
    .email('Invalid email format')
    .max(90, ({ max }) => `Business Email should not exceed ${max} characters`),
  [FormFields.primaryLanguageId]: yup
    .string()
    .trim()
    .max(20, ({ max }) => `Primary Language ID should not exceed ${max} characters`),
  [FormFields.primaryLanguageText]: yup
    .string()
    .trim()
    .max(200, ({ max }) => `Primary Language should not exceed ${max} characters`),
  [FormFields.maritalStatus]: yup
    .string()
    .trim()
    .max(15, ({ max }) => `Marital Status should not exceed ${max} characters`),
  [FormFields.religion]: yup
    .string()
    .trim()
    .max(10, ({ max }) => `Religion should not exceed ${max} characters`),
  [FormFields.ssn]: yup
    .string()
    .trim()
    .max(75, ({ max }) => `SSN should not exceed ${max} characters`),
  [FormFields.dln]: yup
    .string()
    .trim()
    .max(75, ({ max }) => `DLN should not exceed ${max} characters`),
  [FormFields.ethnicGroupId]: yup
    .string()
    .trim()
    .max(20, ({ max }) => `Ethnic Group ID should not exceed ${max} characters`),
  [FormFields.ethnicGroupText]: yup
    .string()
    .trim()
    .max(200, ({ max }) => `Ethnic Group should not exceed ${max} characters`),
  [FormFields.publicityIndicator]: yup
    .string()
    .trim()
    .max(1, ({ max }) => `Publicity Indicator should not exceed ${max} characters`),
});

export const mapApi2Form = (apiData) => {
  if (!apiData || Object.keys(apiData).length === 0) return {};

  return {
    id: apiData.id,
    [FormFields.siteCode]: apiData.siteCode || '',
    [FormFields.mrn]: apiData.mrn || '',
    [FormFields.sendingFacility]: apiData.sendingFacility || '',
    [FormFields.alternateMrn]: apiData.alternateMrn || '',
    [FormFields.familyName]: apiData.familyName || '',
    [FormFields.givenName]: apiData.givenName || '',
    [FormFields.middleName]: apiData.middleName || '',
    [FormFields.prefix]: apiData.prefix || '',
    [FormFields.suffix]: apiData.suffix || '',
    [FormFields.dob]: apiData.dob || '',
    [FormFields.gender]: apiData.gender || '',
    [FormFields.raceId]: apiData.raceId || '',
    [FormFields.raceText]: apiData.raceText || '',
    [FormFields.street]: apiData.street || '',
    [FormFields.city]: apiData.city || '',
    [FormFields.province]: apiData.province || '',
    [FormFields.zip]: apiData.zip || '',
    [FormFields.countryCode]: apiData.countryCode || '',
    [FormFields.phoneNumberHome]: apiData.phoneNumberHome || '',
    [FormFields.emailAddressHome]: apiData.emailAddressHome || '',
    [FormFields.phoneNumberBusiness]: apiData.phoneNumberBusiness || '',
    [FormFields.emailAddressBusiness]: apiData.emailAddressBusiness || '',
    [FormFields.primaryLanguageId]: apiData.primaryLanguageId || '',
    [FormFields.primaryLanguageText]: apiData.primaryLanguageText || '',
    [FormFields.maritalStatus]: apiData.maritalStatus || '',
    [FormFields.religion]: apiData.religion || '',
    [FormFields.ssn]: apiData.ssn || '',
    [FormFields.dln]: apiData.dln || '',
    [FormFields.ethnicGroupId]: apiData.ethnicGroupId || '',
    [FormFields.ethnicGroupText]: apiData.ethnicGroupText || '',
    [FormFields.publicityIndicator]: apiData.publicityIndicator || '',
  };
};

export const mapForm2Api = (formData) => {
  const payload = {};

  // Helper to add non-empty values
  const addIfNotEmpty = (key, value) => {
    let normalizedValue = value;
    if (value instanceof Date) {
      // Serialize to a local date-only string so the picked calendar day is
      // preserved (avoids the UTC off-by-one from JSON.stringify/toISOString).
      normalizedValue = Number.isNaN(value.getTime())
        ? ''
        : formatToBrowserDate(value);
    } else if (typeof value === 'string') {
      normalizedValue = value.trim();
    }
    if (
      normalizedValue !== null &&
      normalizedValue !== undefined &&
      normalizedValue !== ''
    ) {
      payload[key] = normalizedValue;
    }
  };

  // Include id for updates
  if (formData.id) {
    payload.id = formData.id;
  }

  // Include siteCode and mrn only for create (when no id)
  if (!formData.id) {
    addIfNotEmpty('siteCode', formData[FormFields.siteCode]);
    addIfNotEmpty('mrn', formData[FormFields.mrn]);
  }

  // Add all other fields only if they have values
  addIfNotEmpty('sendingFacility', formData[FormFields.sendingFacility]);
  addIfNotEmpty('alternateMrn', formData[FormFields.alternateMrn]);
  addIfNotEmpty('familyName', formData[FormFields.familyName]);
  addIfNotEmpty('givenName', formData[FormFields.givenName]);
  addIfNotEmpty('middleName', formData[FormFields.middleName]);
  addIfNotEmpty('prefix', formData[FormFields.prefix]);
  addIfNotEmpty('suffix', formData[FormFields.suffix]);
  addIfNotEmpty('dob', formData[FormFields.dob]);
  addIfNotEmpty('gender', formData[FormFields.gender]);
  addIfNotEmpty('raceId', formData[FormFields.raceId]);
  addIfNotEmpty('raceText', formData[FormFields.raceText]);
  addIfNotEmpty('street', formData[FormFields.street]);
  addIfNotEmpty('city', formData[FormFields.city]);
  addIfNotEmpty('province', formData[FormFields.province]);
  addIfNotEmpty('zip', formData[FormFields.zip]);
  addIfNotEmpty('countryCode', formData[FormFields.countryCode]);
  addIfNotEmpty('phoneNumberHome', formData[FormFields.phoneNumberHome]);
  addIfNotEmpty('emailAddressHome', formData[FormFields.emailAddressHome]);
  addIfNotEmpty('phoneNumberBusiness', formData[FormFields.phoneNumberBusiness]);
  addIfNotEmpty('emailAddressBusiness', formData[FormFields.emailAddressBusiness]);
  addIfNotEmpty('primaryLanguageId', formData[FormFields.primaryLanguageId]);
  addIfNotEmpty('primaryLanguageText', formData[FormFields.primaryLanguageText]);
  addIfNotEmpty('maritalStatus', formData[FormFields.maritalStatus]);
  addIfNotEmpty('religion', formData[FormFields.religion]);
  addIfNotEmpty('ssn', formData[FormFields.ssn]);
  addIfNotEmpty('dln', formData[FormFields.dln]);
  addIfNotEmpty('ethnicGroupId', formData[FormFields.ethnicGroupId]);
  addIfNotEmpty('ethnicGroupText', formData[FormFields.ethnicGroupText]);
  addIfNotEmpty('publicityIndicator', formData[FormFields.publicityIndicator]);

  return payload;
};
